import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { Plus, Loader2, Check, AlertCircle, Copy } from 'lucide-react';
import LegalLinks from './LegalLinks';
import { api } from '../lib/api';

const MIN_TOPUP = 30;

const presetAmounts = [
  { credits: 30, label: 'One Month' },
  { credits: 60, label: 'Two Months' },
  { credits: 180, label: 'Half Year' },
  { credits: 360, label: 'Full Year' },
];

const sessionKey = (userId) => `harday_topup_session_${userId || 'anon'}`;

const readSession = (userId) => {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(sessionKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.orderNote === 'string' &&
      typeof parsed.upiId === 'string' &&
      Number.isFinite(Number(parsed.amount))
    ) {
      // Backwards-compat: older sessions may have `upiIntentUrl`; we just
      // ignore it (the UPI deeplink flow has been removed).
      return {
        amount: parsed.amount,
        orderNote: parsed.orderNote,
        upiId: parsed.upiId,
        payeeName: parsed.payeeName || '',
        allocatedAt: parsed.allocatedAt || '',
        // Draft of the payer-side UPI ID the user enters before "I Paid".
        // Persisted so a reload / Chrome-close mid-flow does not lose it.
        payerUpiIdDraft: typeof parsed.payerUpiIdDraft === 'string' ? parsed.payerUpiIdDraft : '',
      };
    }
  } catch {
    // ignore
  }
  return null;
};

const writeSession = (userId, payload) => {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(sessionKey(userId), JSON.stringify(payload));
  } catch {
    // ignore
  }
};

const clearSession = (userId) => {
  if (!userId || typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(sessionKey(userId));
  } catch {
    // ignore
  }
};

export const hasPendingTopupSession = (userId) => Boolean(readSession(userId));

const CreditsModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [amount, setAmount] = useState(MIN_TOPUP);
  const [allocating, setAllocating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [requestInfo, setRequestInfo] = useState(null);
  const [submittedFlash, setSubmittedFlash] = useState(false);
  // Payer-side UPI ID — entered by the user before pressing "I Paid" so the
  // admin can match the incoming payment in their UPI / bank inbox.
  const [payerUpiId, setPayerUpiId] = useState('');
  const [copiedKey, setCopiedKey] = useState('');

  // Hydrate from localStorage every time the modal opens (or user changes)
  // so that a refresh / reopen restores the same payment session.
  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setSubmittedFlash(false);
    setCopiedKey('');
    const stored = readSession(user?.id);
    if (stored) {
      setRequestInfo(stored);
      setPayerUpiId(stored.payerUpiIdDraft || '');
    } else {
      setRequestInfo(null);
      setPayerUpiId('');
      setAmount(MIN_TOPUP);
    }
  }, [isOpen, user?.id]);

  // Persist the payer-UPI-ID draft alongside the active session so a refresh
  // / Chrome-close mid-typing doesn't lose it.
  useEffect(() => {
    if (!isOpen || !user?.id || !requestInfo) return;
    const next = { ...requestInfo, payerUpiIdDraft: payerUpiId };
    writeSession(user.id, next);
    // Avoid resetting requestInfo state on every keystroke — only the
    // localStorage copy needs the draft. State is hydrated on next open.
  }, [payerUpiId, isOpen, user?.id, requestInfo]);

  const handleCopy = async (key, text) => {
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(text));
      } else {
        const ta = document.createElement('textarea');
        ta.value = String(text);
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((cur) => (cur === key ? '' : cur)), 1500);
    } catch {
      // ignore — user can long-press to copy manually
    }
  };

  const handleAmountChange = (event) => {
    const raw = event.target.value;
    const parsed = parseInt(raw, 10);
    setAmount(Number.isFinite(parsed) ? parsed : 0);
    setError('');
  };

  // Closes the modal AND discards the stored payment session. Used by both
  // the X button and the explicit Cancel button per spec.
  const cancelAndClose = () => {
    if (allocating || confirming) return;
    clearSession(user?.id);
    setRequestInfo(null);
    setPayerUpiId('');
    setError('');
    onClose();
  };

  const handleOpenAttempt = (open) => {
    if (open) return;
    cancelAndClose();
  };

  // Stage 1: allocate UPI ID + order note (NO admin record yet). The session
  // is persisted locally so it survives refreshes and device hibernation
  // until the user explicitly confirms or cancels. The UPI deeplink/intent
  // flow has been removed — we now just SHOW the assigned UPI ID and let
  // the user pay manually with whichever app they prefer.
  const handleStartTopUp = async () => {
    setError('');
    if (!user) {
      setError('Please sign in to top up your wallet.');
      return;
    }
    const rupees = Number(amount);
    if (!Number.isInteger(rupees) || rupees < MIN_TOPUP) {
      setError(`Minimum top-up is ₹${MIN_TOPUP}.`);
      return;
    }
    setAllocating(true);

    // Allocate with one transparent retry if the first attempt times out (the
    // axios per-request timeout is generous, but flaky mobile networks can
    // still drop the first packet — retrying once gives a much smoother UX
    // than asking the user to tap "Top-up" again).
    const allocateOnce = () => api.allocateTopupRequest(user.id, rupees);
    let lastErr = null;
    let response = null;
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        response = await allocateOnce();
        lastErr = null;
        break;
      } catch (err) {
        lastErr = err;
        const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
        if (!isTimeout || attempt === 1) break; // only retry once on timeout
      }
    }

    if (lastErr || !response) {
      const err = lastErr || {};
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
      let detail;
      if (isTimeout) {
        detail = 'Network is slow. Please check your connection and try again.';
      } else {
        const apiDetail = err?.response?.data?.detail || err?.message;
        detail = typeof apiDetail === 'string' && apiDetail ? apiDetail : 'Failed to start top-up. Please try again.';
      }
      setError(detail);
      setAllocating(false);
      return;
    }

    try {
      const data = response?.data || {};
      const session = {
        amount: data?.amount ?? rupees,
        orderNote: data?.orderNote,
        upiId: data?.upiId,
        payeeName: data?.payeeName,
        allocatedAt: data?.allocatedAt || new Date().toISOString(),
        payerUpiIdDraft: '',
      };
      writeSession(user.id, session);
      setRequestInfo(session);
      setPayerUpiId('');
    } finally {
      setAllocating(false);
    }
  };

  // Stage 2: persist the request to the admin queue (only after user payment).
  // The user must enter the UPI ID they paid from so admin can reconcile.
  const handleIPaid = async () => {
    if (!requestInfo || !user) return;
    setError('');
    const trimmedPayer = (payerUpiId || '').trim();
    if (!trimmedPayer || !trimmedPayer.includes('@') || trimmedPayer.length > 256) {
      setError('Please enter the UPI ID you paid from (e.g. yourname@bank).');
      return;
    }
    setConfirming(true);
    try {
      await api.confirmTopupRequest(user.id, {
        amount: Number(requestInfo.amount),
        orderNote: requestInfo.orderNote,
        upiId: requestInfo.upiId,
        payerUpiId: trimmedPayer,
      });
      clearSession(user.id);
      setSubmittedFlash(true);
      setTimeout(() => {
        setSubmittedFlash(false);
        setRequestInfo(null);
        setPayerUpiId('');
        onClose();
      }, 1500);
    } catch (err) {
      const isTimeout = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
      let detail;
      if (isTimeout) {
        detail = 'Network is slow. Please check your connection and try again.';
      } else {
        const apiDetail = err?.response?.data?.detail || err?.message;
        detail = typeof apiDetail === 'string' && apiDetail ? apiDetail : 'Could not submit confirmation';
      }
      setError(detail);
    } finally {
      setConfirming(false);
    }
  };

  const submitDisabled = allocating || amount < MIN_TOPUP;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenAttempt}>
      <DialogContent className="max-w-md glass-panel border-white/40">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-2xl text-[#000000]">
            Top-up Credits
          </DialogTitle>
        </DialogHeader>

        {!requestInfo ? (
          <div className="space-y-6">
            <p className="text-sm text-[#666666] font-['DM_Sans']">
              ₹1 = 1 Credit · Minimum top-up: ₹{MIN_TOPUP}
            </p>

            <div className="grid grid-cols-2 gap-3">
              {presetAmounts.map((preset) => (
                <button
                  key={preset.credits}
                  type="button"
                  onClick={() => {
                    setAmount(preset.credits);
                    setError('');
                  }}
                  disabled={allocating}
                  data-testid={`topup-preset-${preset.credits}`}
                  className={`p-4 rounded-[16px] text-center transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                    amount === preset.credits
                      ? 'bg-[#9333EA] text-white'
                      : 'glass-panel text-[#000000]'
                  }`}
                >
                  <p className="text-lg font-bold font-['Outfit']">₹{preset.credits}</p>
                  <p
                    className={`text-xs font-['DM_Sans'] ${
                      amount === preset.credits ? 'text-white/80' : 'text-[#666666]'
                    }`}
                  >
                    {preset.credits} credits · {preset.label}
                  </p>
                </button>
              ))}
            </div>

            <div>
              <label className="text-sm text-[#666666] mb-2 block font-['DM_Sans']">
                Custom Amount (₹)
              </label>
              <input
                type="number"
                value={amount}
                onChange={handleAmountChange}
                min={MIN_TOPUP}
                step="1"
                disabled={allocating}
                data-testid="topup-custom-input"
                className="w-full p-3 rounded-[16px] glass-panel text-[#000000] font-semibold text-lg focus:outline-none focus:ring-2 focus:ring-[#9333EA] disabled:opacity-60"
              />
            </div>

            {error ? (
              <p
                data-testid="topup-error"
                className="text-sm text-red-600 font-['DM_Sans'] bg-red-50 border border-red-100 rounded-[12px] px-3 py-2"
              >
                {error}
              </p>
            ) : null}

            <button
              type="button"
              onClick={handleStartTopUp}
              disabled={submitDisabled}
              data-testid="topup-submit-btn"
              className="w-full bg-gradient-to-r from-[#9333EA] via-[#EC4899] via-[#FBBF24] to-[#EF4444] text-white py-3 rounded-full font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {allocating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Allocating UPI ID…
                </>
              ) : (
                <>
                  <Plus className="w-5 h-5" />
                  Top-up ₹{amount}
                </>
              )}
            </button>

            <button
              type="button"
              onClick={cancelAndClose}
              disabled={allocating}
              data-testid="topup-cancel-btn"
              className="w-full py-3 rounded-full border border-[#9333EA]/30 text-[#9333EA] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>

            <p className="text-[11px] text-[#666666] font-['DM_Sans'] text-center">
              We'll show you our UPI ID. Pay manually using any UPI app, then return here and
              press <span className="font-semibold">I Paid</span> to submit your top-up request.
            </p>

            <div className="pt-2 border-t border-gray-100">
              <LegalLinks
                align="center"
                tone="muted"
                prefix="By proceeding, you agree to our"
              />
            </div>
          </div>
        ) : submittedFlash ? (
          <div
            className="space-y-5 py-2"
            data-testid="topup-request-confirmation-success"
          >
            <div className="rounded-[16px] bg-emerald-50 border border-emerald-100 p-4 flex items-start gap-3">
              <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-700 font-['Outfit']">
                  Submitted for verification
                </p>
                <p className="text-xs text-emerald-700/80 font-['DM_Sans'] mt-1">
                  Your wallet will be credited once an admin verifies the payment.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4" data-testid="topup-request-confirmation">
            <div className="rounded-[16px] bg-emerald-50 border border-emerald-100 p-4 flex items-start gap-3">
              <Check className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-emerald-700 font-['Outfit']">
                  Pay manually to the UPI ID below
                </p>
                <p className="text-xs text-emerald-700/80 font-['DM_Sans'] mt-1">
                  Open any UPI app (GPay / PhonePe / Paytm / your bank app), pay ₹
                  {requestInfo.amount} to the UPI ID shown, then come back here, enter the UPI
                  ID you paid <span className="font-semibold">from</span>, and tap
                  <span className="font-semibold"> I Paid</span>.
                </p>
              </div>
            </div>

            <div className="rounded-[16px] glass-panel p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs text-[#666666] font-['DM_Sans']">Amount</p>
                <p className="text-base font-bold text-[#000000] font-['Outfit']">
                  ₹{requestInfo.amount}
                </p>
              </div>

              <div className="border-t border-gray-100" />

              <div>
                <p className="text-xs text-[#666666] font-['DM_Sans'] mb-1">Pay to UPI ID</p>
                <div className="flex items-center gap-2">
                  <code
                    data-testid="topup-payee-upi-id"
                    className="flex-1 min-w-0 truncate text-sm font-semibold text-[#000000] bg-white/60 border border-gray-200 rounded-[10px] px-3 py-2 font-['DM_Sans']"
                    title={requestInfo.upiId}
                  >
                    {requestInfo.upiId}
                  </code>
                  <button
                    type="button"
                    onClick={() => handleCopy('upi', requestInfo.upiId)}
                    data-testid="topup-copy-upi-btn"
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-[10px] bg-[#9333EA] text-white text-xs font-semibold disabled:opacity-60"
                  >
                    {copiedKey === 'upi' ? (
                      <>
                        <Check className="w-3.5 h-3.5" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" /> Copy
                      </>
                    )}
                  </button>
                </div>
                {requestInfo.payeeName ? (
                  <p className="text-[11px] text-[#666666] font-['DM_Sans'] mt-1">
                    Payee name in your UPI app: <span className="font-semibold">{requestInfo.payeeName}</span>
                  </p>
                ) : null}
              </div>

              <div>
                <p className="text-xs text-[#666666] font-['DM_Sans'] mb-1">
                  Order reference (optional, helps faster matching)
                </p>
                <div className="flex items-center gap-2">
                  <code
                    data-testid="topup-order-note"
                    className="flex-1 min-w-0 truncate text-sm font-semibold text-[#000000] bg-white/60 border border-gray-200 rounded-[10px] px-3 py-2 font-['DM_Sans']"
                  >
                    {requestInfo.orderNote}
                  </code>
                  <button
                    type="button"
                    onClick={() => handleCopy('note', requestInfo.orderNote)}
                    data-testid="topup-copy-note-btn"
                    className="shrink-0 inline-flex items-center gap-1 px-3 py-2 rounded-[10px] bg-white border border-[#9333EA]/40 text-[#9333EA] text-xs font-semibold"
                  >
                    {copiedKey === 'note' ? (
                      <>
                        <Check className="w-3.5 h-3.5" /> Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" /> Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="topup-payer-upi-id"
                className="text-sm text-[#000000] mb-2 block font-['DM_Sans'] font-semibold"
              >
                UPI ID you paid <span className="text-[#9333EA]">from</span>
              </label>
              <input
                id="topup-payer-upi-id"
                data-testid="topup-payer-upi-id-input"
                type="text"
                inputMode="email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                value={payerUpiId}
                onChange={(e) => {
                  setPayerUpiId(e.target.value);
                  setError('');
                }}
                placeholder="e.g. yourname@okicici"
                disabled={confirming}
                className="w-full p-3 rounded-[16px] glass-panel text-[#000000] font-semibold text-base focus:outline-none focus:ring-2 focus:ring-[#9333EA] disabled:opacity-60"
              />
              <p className="text-[11px] text-[#666666] font-['DM_Sans'] mt-1">
                Required so the admin can match your payment in their bank / UPI inbox.
              </p>
            </div>

            <div className="rounded-[12px] bg-amber-50 border border-amber-100 px-3 py-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
              <p className="text-[11px] text-amber-700 font-['DM_Sans']">
                Only press <span className="font-semibold">I Paid</span> after you have
                actually completed the payment in your UPI app.
              </p>
            </div>

            {error ? (
              <p
                data-testid="topup-error"
                className="text-sm text-red-600 font-['DM_Sans'] bg-red-50 border border-red-100 rounded-[12px] px-3 py-2"
              >
                {error}
              </p>
            ) : null}

            <button
              type="button"
              onClick={handleIPaid}
              disabled={confirming || !payerUpiId.trim() || !payerUpiId.includes('@')}
              data-testid="topup-i-paid-btn"
              className="w-full py-3 rounded-full bg-gradient-to-r from-[#9333EA] to-[#EC4899] text-white font-semibold disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {confirming ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Submitting…
                </>
              ) : (
                'I Paid'
              )}
            </button>

            <button
              type="button"
              onClick={cancelAndClose}
              disabled={confirming}
              data-testid="topup-cancel-btn"
              className="w-full py-3 rounded-full border border-[#9333EA]/30 text-[#9333EA] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreditsModal;
